import React, { useState, useEffect, useRef } from 'react';
import { X, Paperclip } from 'lucide-react'; // This component's personal "shopping list"

const ComposeEmailModal = ({ isOpen, onClose, handleSendEmail, initialTo = '', initialSubject = '', initialBody = '' }) => {
    const [to, setTo] = useState(initialTo);
    const [cc, setCc] = useState('');
    const [bcc, setBcc] = useState('');
    const [subject, setSubject] = useState(initialSubject);
    const [body, setBody] = useState(initialBody);
    const [attachments, setAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        setTo(initialTo);
        setSubject(initialSubject);
        setBody(initialBody);
        setCc('');
        setBcc('');
        setAttachments([]);
    }, [initialTo, initialSubject, initialBody, isOpen]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = [];

        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = () => {
                newAttachments.push({
                    name: file.name,
                    contentType: file.type,
                    contentBytes: reader.result.split(',')[1] // Get base64 content
                });
                if (newAttachments.length === files.length) {
                    setAttachments(prev => [...prev, ...newAttachments]);
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSend = async (e) => {
        e.preventDefault();
        setIsSending(true);
        await handleSendEmail({ to, cc, bcc, subject, body, attachments });
        setIsSending(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60]">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold">New Message</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><X size={20}/></button>
                </div>
                <form onSubmit={handleSend}>
                    <div className="flex justify-between items-center">
                        <input type="email" placeholder="To" value={to} onChange={e => setTo(e.target.value)} className="w-full p-2 border-b" required />
                        <button type="button" onClick={() => setShowCcBcc(!showCcBcc)} className="ml-2 text-sm text-gray-500">Cc/Bcc</button>
                    </div>
                    {showCcBcc && (
                        <>
                            <div className="mt-2"><input type="email" placeholder="Cc" value={cc} onChange={e => setCc(e.target.value)} className="w-full p-2 border-b" /></div>
                            <div className="mt-2"><input type="email" placeholder="Bcc" value={bcc} onChange={e => setBcc(e.target.value)} className="w-full p-2 border-b" /></div>
                        </>
                    )}
                    <div className="mt-2"><input type="text" placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} className="w-full p-2 border-b" /></div>
                    <div className="my-4"><textarea rows="10" value={body} onChange={e => setBody(e.target.value)} className="w-full p-2 border rounded-md" placeholder="Your message..."></textarea></div>
                    
                    <div className="my-4">
                        {attachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded-md mb-1">
                                <span className="text-sm">{file.name}</span>
                                <button type="button" onClick={() => removeAttachment(index)} className="p-1 text-red-500 hover:bg-red-100 rounded-full"><X size={16}/></button>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-between">
                        <button type="button" onClick={() => fileInputRef.current.click()} className="p-2 hover:bg-gray-200 rounded-md"><Paperclip size={20}/></button>
                        <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                        <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700" disabled={isSending}>
                            {isSending ? 'Sending...' : 'Send'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default ComposeEmailModal;